import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit2, Trash2, Search, ChevronDown } from 'lucide-react';

interface Service {
  id: string;
  code: string;
  description: string;
  rate: number;
  duration: number;
  isDefault: boolean;
  billInUnits: boolean;
  availableOnline: boolean;
  allowNewClients: boolean;
  requireCallToRequest: boolean;
  blockOffBefore: number;
  blockOffAfter: number;
}

interface ServicesTabProps {
  services: Service[];
  setServices: React.Dispatch<React.SetStateAction<Service[]>>;
}

interface ServiceCode {
  code: string;
  description: string;
  category: 'psychotherapy' | 'evaluation' | 'telehealth' | 'group' | 'other';
  duration?: number;
}

const SERVICE_CODES: ServiceCode[] = [
  // Psychotherapy
  { code: '90832', description: 'Psychotherapy, 30 min', category: 'psychotherapy', duration: 30 },
  { code: '90834', description: 'Psychotherapy, 45 min', category: 'psychotherapy', duration: 45 },
  { code: '90837', description: 'Psychotherapy, 60 min', category: 'psychotherapy', duration: 60 },
  { code: '90838', description: 'Psychotherapy Add-on, 60 min', category: 'psychotherapy', duration: 60 },
  { code: '90846', description: 'Family Psychotherapy without patient present', category: 'psychotherapy', duration: 50 },
  { code: '90847', description: 'Family psychotherapy, conjoint psychotherapy with the patient present', category: 'psychotherapy', duration: 50 },

  // Group Therapy
  { code: '90849', description: 'Multiple-family group psychotherapy', category: 'group', duration: 90 },
  { code: '90853', description: 'Group psychotherapy (other than a multiple-family group)', category: 'group', duration: 90 },

  // Evaluation & Assessment
  { code: '90791', description: 'Psychiatric Diagnostic Evaluation', category: 'evaluation', duration: 60 },
  { code: '90792', description: 'Psychiatric Diagnostic Evaluation with Medical', category: 'evaluation', duration: 90 },
  { code: '96105', description: 'Assessment of Aphasia', category: 'evaluation', duration: 60 },
  { code: '96116', description: 'Neurobehavioral status exam, first hour', category: 'evaluation', duration: 60 },
  { code: '90885', description: 'Psychiatric evaluation of records, reports, psychometric and/or projective tests, and other accumulated data for medical diagnostic purposes', category: 'evaluation', duration: 45 },

  // Telehealth
  { code: '99446', description: 'Interprofessional telephone/Internet/electronic health record assessment and management service provided by a consultative physician or other qualified health care professional; 5-10 minutes of medical consultative discussion and review', category: 'telehealth', duration: 10 },
  { code: '99447', description: 'Interprofessional telephone/Internet/electronic health record assessment and management service provided by a consultative physician or other qualified health care professional; 11-20 minutes of medical consultative discussion and review', category: 'telehealth', duration: 20 },
  { code: '99448', description: 'Interprofessional telephone/Internet/electronic health record assessment and management service provided by a consultative physician or other qualified health care professional; 21-30 minutes of medical consultative discussion and review', category: 'telehealth', duration: 30 },
  { code: '99449', description: 'Interprofessional telephone/Internet/electronic health record assessment and management service provided by a consultative physician or other qualified health care professional; 31 minutes or more of medical consultative discussion and review', category: 'telehealth', duration: 45 },

];

const ServicesTab: React.FC<ServicesTabProps> = ({ services, setServices }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<string | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState<Partial<Service>>({
    code: '',
    description: '',
    rate: undefined,
    duration: 50,
    isDefault: false,
    billInUnits: false,
    availableOnline: true,
    allowNewClients: false,
    requireCallToRequest: false,
    blockOffBefore: 0,
    blockOffAfter: 0
  });

  const handleAddService = () => {
    setEditingService(null);
    setFormData({
      code: '',
      description: '',
      rate: undefined,
      duration: 50,
      isDefault: false,
      billInUnits: false,
      availableOnline: true,
      allowNewClients: false,
      requireCallToRequest: false,
      blockOffBefore: 0,
      blockOffAfter: 0
    });
    setShowAddForm(true);
    setShowDropdown(false);
    setSearchTerm('');
    setSelectedCategory('all');
  };

  const handleEditService = (service: Service) => {
    setEditingService(service);
    setFormData(service);
    setShowAddForm(true);
  };

  const handleSaveService = () => {
    if (editingService) {
      setServices(services.map(s => s.id === editingService.id ? { ...formData, id: editingService.id, rate: formData.rate || 0 } as Service : s));
    } else {
      const newService: Service = {
        ...formData,
        id: Date.now().toString(),
        rate: formData.rate || 0
      } as Service;
      setServices([...services, newService]);
    }
    setShowAddForm(false);
    setEditingService(null);
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingService(null);
    setShowDropdown(false);
    setSearchTerm('');
    setSelectedCategory('all');
  };

  const handleDeleteService = (serviceId: string) => {
    setServiceToDelete(serviceId);
    setShowDeleteModal(true);
  };

  const confirmDeleteService = () => {
    if (serviceToDelete) {
      setServices(services.filter(s => s.id !== serviceToDelete));
    }
    setShowDeleteModal(false);
    setServiceToDelete(null);
  };

  const cancelDeleteService = () => {
    setShowDeleteModal(false);
    setServiceToDelete(null);
  };

  const updateFormData = (field: keyof Service, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const filteredServiceCodes = SERVICE_CODES.filter(serviceCode => {
    const matchesSearch = searchTerm === '' ||
      serviceCode.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      serviceCode.description.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = selectedCategory === 'all' || serviceCode.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const handleServiceCodeSelect = (serviceCode: ServiceCode) => {
    updateFormData('code', serviceCode.code);
    if (!formData.description) {
      updateFormData('description', serviceCode.description);
    }
    if (serviceCode.duration && !formData.duration) {
      updateFormData('duration', serviceCode.duration);
    }
    setShowDropdown(false);
    setSearchTerm('');
  };

  const categories = [
    { id: 'all', label: 'All Services' },
    { id: 'psychotherapy', label: 'Psychotherapy' },
    { id: 'evaluation', label: 'Evaluation & Assessment' },
    { id: 'telehealth', label: 'Telehealth' },
    { id: 'group', label: 'Group Therapy' }
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div style={{
      backgroundColor: '#28527A',
      borderRadius: '8px',
      padding: '2rem',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
      border: '1px solid #6CA6C1'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem'
      }}>
        <div>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#F7E9A0',
            margin: '0 0 0.5rem 0'
          }}>
            Services
          </h2>
          <p style={{
            color: '#e5e7eb',
            margin: 0,
            fontSize: '0.875rem'
          }}>
            Manage services and set rates
          </p>
        </div>
        <button
          onClick={handleAddService}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            backgroundColor: '#3B82F6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            padding: '0.75rem 1rem',
            fontSize: '0.875rem',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563EB'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3B82F6'}
        >
          <Plus size={16} />
          Add service
        </button>
      </div>

      {/* Add/Edit Service Form */}
      {showAddForm && (
        <div style={{
          backgroundColor: '#E5F3FF',
          borderRadius: '8px',
          padding: '1.5rem',
          marginBottom: '2rem',
          border: '1px solid #B3D4FC'
        }}>
          <h3 style={{
            fontSize: '1.125rem',
            fontWeight: '600',
            color: '#1F2937',
            margin: '0 0 1rem 0'
          }}>
            {editingService ? 'Edit Service' : 'Add New Service'}
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div ref={dropdownRef} style={{ position: 'relative' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                Service
              </label>
              <div
                onClick={() => setShowDropdown(!showDropdown)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '2px solid #3B82F6',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  minHeight: '36px'
                }}
              >
                <span style={{ color: formData.code ? '#374151' : '#9CA3AF' }}>
                  {formData.code || 'Select service code'}
                </span>
                <ChevronDown
                  size={16}
                  style={{
                    transform: showDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                    color: '#6B7280',
                    flexShrink: 0
                  }}
                />
              </div>

              {/* Enhanced Dropdown */}
              {showDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #D1D5DB',
                  borderRadius: '4px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000,
                  maxHeight: '400px',
                  overflow: 'hidden'
                }}>
                  {/* Search Input */}
                  <div style={{ padding: '0.75rem', borderBottom: '1px solid #E5E7EB' }}>
                    <div style={{ position: 'relative' }}>
                      <Search size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
                      <input
                        type="text"
                        placeholder="Search service codes"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.5rem 0.5rem 0.5rem 2.5rem',
                          border: '1px solid #D1D5DB',
                          borderRadius: '4px',
                          fontSize: '0.875rem'
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>

                  {/* Category Filters */}
                  <div style={{ padding: '0.5rem', borderBottom: '1px solid #E5E7EB', display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                    {categories.map(category => (
                      <button
                        key={category.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCategory(category.id);
                        }}
                        style={{
                          padding: '0.25rem 0.5rem',
                          fontSize: '0.75rem',
                          border: 'none',
                          borderRadius: '12px',
                          backgroundColor: selectedCategory === category.id ? '#3B82F6' : '#F3F4F6',
                          color: selectedCategory === category.id ? 'white' : '#374151',
                          cursor: 'pointer'
                        }}
                      >
                        {category.label}
                      </button>
                    ))}
                  </div>

                  {/* Service Options List */}
                  <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                    {filteredServiceCodes.length > 0 ? (
                      filteredServiceCodes.map(serviceCode => (
                        <div
                          key={serviceCode.code}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleServiceCodeSelect(serviceCode);
                          }}
                          style={{
                            padding: '0.2rem 0.75rem',
                            borderBottom: '1px solid #F3F4F6',
                            cursor: 'pointer',
                            backgroundColor: formData.code === serviceCode.code ? '#EBF8FF' : 'white'
                          }}
                          onMouseEnter={(e) => {
                            if (formData.code !== serviceCode.code) {
                              e.currentTarget.style.backgroundColor = '#F9FAFB';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (formData.code !== serviceCode.code) {
                              e.currentTarget.style.backgroundColor = 'white';
                            }
                          }}
                        >
                          <div style={{ fontSize: '0.875rem', color: '#374151', lineHeight: '1.3' }}>
                            <span style={{ fontWeight: '600', color: '#3B82F6' }}>{serviceCode.code}</span>
                            <span style={{ color: '#6B7280' }}> - {serviceCode.description}</span>
                            {serviceCode.duration && (
                              <span style={{ color: '#6B7280' }}>, {serviceCode.duration} min</span>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: '1rem', textAlign: 'center', color: '#6B7280' }}>
                        No services found matching your search
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                Description
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => updateFormData('description', e.target.value)}
                placeholder="Description"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #D1D5DB',
                  borderRadius: '4px',
                  fontSize: '0.875rem'
                }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                Rate
              </label>
              <input
                type="number"
                value={formData.rate ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  updateFormData('rate', value === '' ? undefined : parseFloat(value));
                }}
                placeholder="0"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #D1D5DB',
                  borderRadius: '4px',
                  fontSize: '0.875rem'
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'end', gap: '0.5rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                  Default Duration
                </label>
                <input
                  type="number"
                  value={formData.duration}
                  onChange={(e) => updateFormData('duration', parseInt(e.target.value) || 0)}
                  style={{
                    width: '80px',
                    padding: '0.5rem',
                    border: '1px solid #D1D5DB',
                    borderRadius: '4px',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
              <span style={{ fontSize: '0.875rem', color: '#374151', paddingBottom: '0.5rem' }}>min</span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '2rem', marginBottom: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#374151' }}>
              <input
                type="checkbox"
                checked={formData.isDefault}
                onChange={(e) => updateFormData('isDefault', e.target.checked)}
              />
              Make this the default service
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#374151' }}>
              <input
                type="checkbox"
                checked={formData.billInUnits}
                onChange={(e) => updateFormData('billInUnits', e.target.checked)}
              />
              Bill this code in units
            </label>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', margin: '0 0 0.5rem 0' }}>
              Booking Options
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#374151' }}>
                <input
                  type="checkbox"
                  checked={formData.availableOnline}
                  onChange={(e) => updateFormData('availableOnline', e.target.checked)}
                />
                Available for online appointment requests
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#374151' }}>
                <input
                  type="checkbox"
                  checked={formData.allowNewClients}
                  onChange={(e) => updateFormData('allowNewClients', e.target.checked)}
                />
                Allow for New Clients
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#374151' }}>
                <input
                  type="checkbox"
                  checked={formData.requireCallToRequest}
                  onChange={(e) => updateFormData('requireCallToRequest', e.target.checked)}
                />
                Require Clients to call to request available appointment
              </label>
            </div>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '0.875rem', color: '#374151' }}>Block off</span>
              <input
                type="number"
                value={formData.blockOffBefore}
                onChange={(e) => updateFormData('blockOffBefore', parseInt(e.target.value) || 0)}
                style={{
                  width: '60px',
                  padding: '0.25rem',
                  border: '1px solid #D1D5DB',
                  borderRadius: '4px',
                  fontSize: '0.875rem'
                }}
              />
              <span style={{ fontSize: '0.875rem', color: '#374151' }}>minutes before and</span>
              <input
                type="number"
                value={formData.blockOffAfter}
                onChange={(e) => updateFormData('blockOffAfter', parseInt(e.target.value) || 0)}
                style={{
                  width: '60px',
                  padding: '0.25rem',
                  border: '1px solid #D1D5DB',
                  borderRadius: '4px',
                  fontSize: '0.875rem'
                }}
              />
              <span style={{ fontSize: '0.875rem', color: '#374151' }}>minutes after the appointment</span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={handleCancel}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: 'transparent',
                color: '#6B7280',
                border: '1px solid #D1D5DB',
                borderRadius: '4px',
                fontSize: '0.875rem',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSaveService}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#3B82F6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '0.875rem',
                cursor: 'pointer'
              }}
            >
              Save
            </button>
          </div>
        </div>
      )}

      {/* Services List */}
      <div>
        <div style={{
          backgroundColor: '#E5F3FF',
          borderRadius: '8px 8px 0 0',
          padding: '1rem',
          border: '1px solid #B3D4FC',
          borderBottom: 'none'
        }}>
          <h3 style={{
            fontSize: '1rem',
            fontWeight: '600',
            color: '#1F2937',
            margin: '0 0 0.5rem 0'
          }}>
            Click on each Service name to edit
          </h3>
          <p style={{
            fontSize: '0.875rem',
            color: '#6B7280',
            margin: 0
          }}>
            Service Descriptions are shown throughout the ClinicCore platform internally, in some client communications and in superbills.
          </p>
        </div>

        <div style={{
          backgroundColor: 'white',
          border: '1px solid #B3D4FC',
          borderRadius: '0 0 8px 8px'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '100px 1fr auto',
            gap: '1rem',
            padding: '1rem',
            borderBottom: '1px solid #E5E7EB',
            backgroundColor: '#F9FAFB',
            fontSize: '0.875rem',
            fontWeight: '600',
            color: '#374151'
          }}>
            <div>Services</div>
            <div></div>
            <div>Appointment requests</div>
          </div>

          {services.map((service) => (
            <div
              key={service.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '100px 1fr auto',
                gap: '1rem',
                padding: '1rem',
                borderBottom: '1px solid #E5E7EB',
                alignItems: 'start'
              }}
            >
              <div style={{ color: '#3B82F6', fontWeight: '500' }}>
                {service.code}
              </div>
              <div>
                <div
                  onClick={() => handleEditService(service)}
                  style={{
                    color: '#3B82F6',
                    cursor: 'pointer',
                    marginBottom: '0.25rem',
                    textDecoration: 'underline'
                  }}
                >
                  {service.description}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                  {service.duration} minutes at ${service.rate}
                </div>
                {service.isDefault && (
                  <div style={{ fontSize: '0.875rem', color: '#10B981', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    ✓ Default practice service
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => handleEditService(service)}
                  style={{
                    padding: '0.25rem',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#6B7280'
                  }}
                  title="Edit service"
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={() => handleDeleteService(service.id)}
                  style={{
                    padding: '0.25rem',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#EF4444'
                  }}
                  title="Delete service"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#2D3748',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
            border: '1px solid #4A5568'
          }}>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: '#F7FAFC',
              margin: '0 0 1rem 0',
              textAlign: 'center'
            }}>
              Confirm Deletion
            </h3>
            <p style={{
              fontSize: '1rem',
              color: '#E2E8F0',
              margin: '0 0 2rem 0',
              textAlign: 'center',
              lineHeight: '1.5'
            }}>
              Are you sure you want to delete this service?
            </p>
            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center'
            }}>
              <button
                onClick={confirmDeleteService}
                style={{
                  padding: '0.75rem 2rem',
                  backgroundColor: '#718096',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  minWidth: '80px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4A5568'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#718096'}
              >
                OK
              </button>
              <button
                onClick={cancelDeleteService}
                style={{
                  padding: '0.75rem 2rem',
                  backgroundColor: '#3182CE',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  minWidth: '80px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2C5282'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3182CE'}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesTab;